import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/Footer';
import { BookOpen, Zap, ChevronRight, Play, Trophy, User, Clock, ArrowRight } from 'lucide-react';
import ImageWithFallback from '../../components/ImageWithFallback';

const StudentDashboard = () => {
    const { user, isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const [enrolledCourses, setEnrolledCourses] = useState([]);

    // Learning streak
    const [learningStreak] = useState({
        current: 12,
        longest: 45,
        lastActivity: 'Today'
    });

    useEffect(() => {
        if (!isLoggedIn) {
            navigate('/');
            return;
        }

        const fetchEnrolledCourses = async () => {
            try {
                const { getUserEnrollments } = require('../../services/api');
                const result = await getUserEnrollments();
                if (result.success) {
                    setEnrolledCourses(result.data);
                }
            } catch (error) {
                console.error("Error fetching enrolled courses:", error);
                const purchased = JSON.parse(localStorage.getItem('purchasedCourses') || '[]');
                setEnrolledCourses(purchased);
            }
        };

        fetchEnrolledCourses();
    }, [isLoggedIn, navigate]);

    const handleCourseClick = (course) => {
        if (!course) return;
        const targetId = course.course_id || course.id;
        localStorage.setItem('selectedCourse', JSON.stringify(course));
        navigate(`/courseoverview?course_id=${targetId}`);
    };

    const activeCoursesCount = enrolledCourses.filter(c => Number(c.progress || 0) > 0).length;
    const completedCoursesCount = enrolledCourses.filter(c => Number(c.progress || 0) >= 99).length;

    return (
        <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
            <main className="flex-1 max-w-7xl mx-auto px-2.5 sm:px-6 lg:px-8 py-3 sm:py-6 w-full space-y-3 sm:space-y-5">

                {/* Welcome Banner Card (Compact Mobile Height & Typography) */}
                <div className="bg-gradient-to-r from-emerald-800 via-teal-800 to-emerald-900 rounded-xl sm:rounded-2xl p-3 sm:p-6 text-white shadow-xs relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-16 -mt-16 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full -ml-12 -mb-12 pointer-events-none"></div>
                    
                    <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-5">
                        <div className="flex flex-row items-center text-left gap-2.5 sm:gap-4 w-full sm:w-auto">
                            <ImageWithFallback
                                src={user?.image}
                                alt="Profile"
                                className="w-11 h-11 sm:w-16 sm:h-16 rounded-full object-cover border border-white/80 shadow-xs shrink-0"
                                fallbackSrc="/img/course-placeholder.png"
                            />
                            <div className="min-w-0 flex-1">
                                <h1 className="text-xs sm:text-xl font-bold tracking-tight leading-snug truncate">
                                    Welcome back, {user?.name || 'Student'}! 👋
                                </h1>
                                <p className="text-emerald-100/90 text-[10px] sm:text-xs font-normal mt-0.5 truncate">
                                    Ready to continue your learning journey?
                                </p>
                            </div>
                        </div>

                        {/* Learning Streak Pill */}
                        <div className="w-full sm:w-auto shrink-0">
                            <div className="bg-white/15 border border-white/20 rounded-lg px-2.5 py-1.5 sm:px-3.5 sm:py-2 backdrop-blur-md flex items-center justify-between sm:justify-center gap-2">
                                <span className="text-[9px] sm:text-xs text-emerald-200 uppercase font-bold tracking-wider">Streak:</span>
                                <div className="flex items-center gap-1">
                                    <Zap className="fill-amber-400 text-amber-400 shrink-0" size={14} />
                                    <span className="text-xs sm:text-base font-extrabold">{learningStreak.current}</span>
                                    <span className="text-[10px] sm:text-xs text-emerald-100 font-medium">Days</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2x2 Compact Quick Stats Cards Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3.5">
                    {/* Courses Stat */}
                    <div 
                        onClick={() => navigate('/student/mycourses')}
                        className="bg-white rounded-xl p-2.5 sm:p-4 border border-gray-100 shadow-2xs hover:border-emerald-200 transition cursor-pointer flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Enrolled</span>
                            <div className="p-1 sm:p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                                <BookOpen size={14} />
                            </div>
                        </div>
                        <div>
                            <p className="text-sm sm:text-2xl font-extrabold text-gray-900">{enrolledCourses.length}</p>
                            <p className="text-[9px] sm:text-xs text-emerald-600 font-bold mt-0.5 truncate">{activeCoursesCount} Active</p>
                        </div>
                    </div>

                    {/* Active Streak Stat */}
                    <div 
                        onClick={() => navigate('/student/mycourses')}
                        className="bg-white rounded-xl p-2.5 sm:p-4 border border-gray-100 shadow-2xs hover:border-purple-200 transition cursor-pointer flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Streak</span>
                            <div className="p-1 sm:p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                                <Clock size={14} />
                            </div>
                        </div>
                        <div>
                            <p className="text-sm sm:text-2xl font-extrabold text-gray-900">{learningStreak.current}d</p>
                            <p className="text-[9px] sm:text-xs text-purple-600 font-bold mt-0.5 truncate">Active Student</p>
                        </div>
                    </div>

                    {/* Profile Stat */}
                    <div 
                        onClick={() => navigate('/student/profile')}
                        className="bg-white rounded-xl p-2.5 sm:p-4 border border-gray-100 shadow-2xs hover:border-emerald-200 transition cursor-pointer flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Account</span>
                            <div className="p-1 sm:p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                                <User size={14} />
                            </div>
                        </div>
                        <div>
                            <p className="text-xs sm:text-lg font-bold text-gray-900 truncate">Profile</p>
                            <p className="text-[9px] sm:text-xs text-emerald-600 font-bold mt-0.5 truncate">Edit Details</p>
                        </div>
                    </div>

                    {/* Certificates Stat */}
                    <div 
                        onClick={() => navigate('/student/certificates')}
                        className="bg-white rounded-xl p-2.5 sm:p-4 border border-gray-100 shadow-2xs hover:border-amber-200 transition cursor-pointer flex flex-col justify-between"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Certificates</span>
                            <div className="p-1 sm:p-1.5 bg-amber-50 text-amber-600 rounded-lg">
                                <Trophy size={14} />
                            </div>
                        </div>
                        <div>
                            <p className="text-sm sm:text-2xl font-extrabold text-gray-900">{completedCoursesCount}</p>
                            <p className="text-[9px] sm:text-xs text-amber-600 font-bold mt-0.5 truncate">Earned</p>
                        </div>
                    </div>
                </div>

                {/* Continue Learning Section */}
                <div className="space-y-2.5 pt-1">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                        <div>
                            <h2 className="text-xs sm:text-lg font-bold text-gray-900">Continue Learning</h2>
                            <p className="text-[10px] sm:text-xs text-gray-500">Pick up right where you left off</p>
                        </div>
                        <button
                            onClick={() => navigate('/student/mycourses')}
                            className="text-emerald-600 hover:text-emerald-700 font-bold text-[11px] sm:text-xs flex items-center gap-0.5 cursor-pointer"
                        >
                            Browse All <ChevronRight size={14} />
                        </button>
                    </div>

                    {enrolledCourses.length === 0 ? (
                        <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-10 text-center shadow-2xs border border-gray-100 space-y-3">
                            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                                <BookOpen size={24} />
                            </div>
                            <div>
                                <h3 className="text-xs sm:text-base font-bold text-gray-900">No Enrolled Courses Yet</h3>
                                <p className="text-[11px] text-gray-500 max-w-xs mx-auto mt-0.5">Explore our catalog of certified courses and start learning today.</p>
                            </div>
                            <button
                                onClick={() => navigate('/student/mycourses')}
                                className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-emerald-700 transition shadow-2xs cursor-pointer inline-flex items-center gap-1"
                            >
                                Explore Courses <ArrowRight size={14} />
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4">
                            {enrolledCourses.slice(0, 4).map((course) => (
                                <div
                                    key={course.id || course.course_id}
                                    className="bg-white rounded-xl overflow-hidden shadow-2xs border border-gray-100 hover:border-emerald-200 transition flex flex-col justify-between group"
                                >
                                    <div>
                                        <div className="relative h-28 sm:h-36 overflow-hidden">
                                            <ImageWithFallback
                                                src={course.image}
                                                alt={course.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-emerald-600 shadow-xs">
                                                    <Play size={16} className="ml-0.5 fill-current" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-2.5 sm:p-4 space-y-1.5">
                                            <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-2 leading-snug group-hover:text-emerald-600 transition-colors">
                                                {course.title}
                                            </h3>
                                            <p className="text-[10px] sm:text-xs text-gray-500 font-medium">{course.instructor || 'Specialist Trainer'}</p>

                                            {/* Progress Bar */}
                                            <div className="pt-0.5 space-y-1">
                                                <div className="flex justify-between text-[10px] font-bold">
                                                    <span className="text-gray-500">Progress</span>
                                                    <span className="text-emerald-700">{course.progress || 0}%</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                                    <div
                                                        className="bg-emerald-600 h-full rounded-full transition-all"
                                                        style={{ width: `${course.progress || 0}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="p-2.5 sm:p-4 pt-0 flex gap-1.5">
                                        <button
                                            onClick={() => handleCourseClick(course)}
                                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-1.5 sm:py-2 rounded-lg text-xs font-bold transition shadow-2xs flex items-center justify-center gap-1 cursor-pointer"
                                        >
                                            <Play size={12} className="fill-current" />
                                            <span>{Number(course.progress) >= 99 ? 'Review Course' : 'Continue Learning'}</span>
                                        </button>

                                        {Number(course.progress) >= 99 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/student/certificates');
                                                }}
                                                className="bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1.5 sm:py-2 rounded-lg text-xs font-bold transition shadow-2xs flex items-center gap-1 cursor-pointer shrink-0"
                                                title="Get Certificate"
                                            >
                                                <Trophy size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </main>

            <Footer />
        </div>
    );
};

export default StudentDashboard;